import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import axios from "axios";
import {clubFinderSearchBarSliceActions} from "../../../store/slices/clubFinderSearchBarSlice";
import SearchLocationInput
  from '../../atoms/search-bar-filter-components/location/search-location-input/search-location-input';
import SearchLocationResults
  from '../../atoms/search-bar-filter-components/location/search-location-results/search-location-results';
import icon from "../../assets/icons/search-icon-dark.svg";
import {Config, TenantAppVersionConfig} from "../../../constants/Config";
import './search-suggest-location.scss';
import {
  ClubFinderLocationResultTypes,
  ClubFinderLocationSearchResultSetTypes
} from "../../../types/ApiTypeDefinition/clubFinderLocationSearchResultTypes";
import {RootState} from "../../../store/configureStore";
import {IConfig} from "../../../constants/ConfigTypes";


const baseClass = 'clubFinder-search-suggest-location';
const MobileViewWidth = Config.breakpoints.large.width;

interface PropTypes {
  search?: () => void;
  searchButtonPressed?: boolean;
  openFilterDrawer?: (event: any) => void;
  getUserCurrentLocation?: (event: any) => void;
}

const SearchSuggestLocation = (props: PropTypes): JSX.Element => {
  const dispatch = useDispatch()
  const parentWidth = useSelector((state: RootState) => state.widgetConfigurationSettings.widgetParentWidth);
  const googleMapConfig = useSelector((state: RootState) => state.widgetConfigurationSettings.googleMapConfig);
  const tenant = useSelector((state: RootState) => state.widgetConfigurationSettings.tenant);
  const language = useSelector((state: RootState) => state.widgetConfigurationSettings.language);
  const list: ClubFinderLocationSearchResultSetTypes = [];
  const [inputLength, setInputLength] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [dataSet, updateDataSet] = useState(list);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [googleDataSet, updateGoogleDataSet] = useState(list);
  const [dataStatus, setDataStatus] = useState("");
  const [inputFocusStatus, setInputFocusStatus] = useState(false);
  const [isMouseOverResults, setIsMouseOverResults] = useState(false);
  const [isResultItemClicked, setIsResultItemClicked] = useState(false);
  const [inputEditableStatus, setInputEditableStatus] = useState(false);
  const [isSearchResultVisible, setIsSearchResultVisible] = useState(false);
  const [locationInputText, setLocationInputText] = useState(tenant ? Config.locationInputPlaceholder[tenant as keyof IConfig["locationInputPlaceholder"]].desktop_text : '');
  const [pinCodeLocationResults, setPinCodeLocationResults] = useState<string[]|[]>([]);
  const [presetInputValue, setPresetInputValue] = useState('');

  const getRegionsByName = useCallback((resp: any): void => {
    setLoadingRegions(true);
    const suggestions = resp.data;
    if (suggestions && suggestions.places && suggestions.places.predictions && suggestions.places.predictions.length) {
      updateGoogleDataSet(suggestions.places.predictions);
      setLoadingRegions(false);
    } else {
      updateGoogleDataSet([]);
      setLoadingRegions(false);
    }
  }, []);

  const getPlacesByPinCode = useCallback((apiEndpoint: string):void => {
    setLoadingClubs(true);
    updateDataSet([]);
    updateGoogleDataSet([]);
    axios.get(apiEndpoint, {headers: {'tenant': tenant}}).then(async (resp) => {
      // @ts-ignore
      const suggestions = resp.data;
      if(suggestions && suggestions.postCodeSuburbs && suggestions.postCodeSuburbs.length) {
        setPinCodeLocationResults(suggestions.postCodeSuburbs);
        setDataStatus("");
      } else {
        setPinCodeLocationResults([]);
        setDataStatus("No Results found!");
      }
      setLoadingClubs(false);
    }).catch(e => {
      setLoadingClubs(false);
      setPinCodeLocationResults([]);
      setDataStatus("No Results found!");
    });
  },[]);

  const getClubsByName = useCallback((apiEndpoint: string): void => {
    setLoadingClubs(true);
    setPinCodeLocationResults([]);
    axios.get(apiEndpoint, {headers: {'tenant': tenant}}).then(async (resp) => {
      const suggestions = resp.data;
      getRegionsByName(resp);
      if (suggestions && suggestions.organisations && suggestions.organisations.length) {
        updateDataSet(suggestions.organisations);
        setDataStatus("");
        setLoadingClubs(false);
      } else {
        updateDataSet([]);
        setLoadingClubs(false);
        setDataStatus("No Results found!");
      }
    }).catch(e => {
      setLoadingClubs(false);
      setDataStatus("No Results found!");
      console.log('Could not find clubs:', e);
    });
  }, [tenant, getRegionsByName])

  const consolidateDataSets = useCallback(() => {
    const regionData = googleDataSet;
    let regions: ClubFinderLocationResultTypes[] = [];
    if (regionData && regionData && regionData.length) {
      // @ts-ignore
      regions = regionData.map((item: any) => {
        let fullAddress = '';
        if(item.terms) {
          // Complete address except the country, from google results.
          for (let i = 0; i < item.terms.length -1; i++) {
            fullAddress += item.terms[i].value;
            if (i < item.terms.length - 2) {
              fullAddress += ", ";
            }
          }
        }
          return {
            placeId: item.place_id,
            address: {
              suburb: item.terms && item.terms[0] && item.terms[1].value ? item.terms[0].value : item.structured_formatting.main_text,
              state: (item.terms && item.terms[1] && item.terms[1].value) ? item.terms[1].value : null,
              fullAddress: fullAddress
            }
          }
      });
    }
    const finalDataSet = [
      ...dataSet,
      ...regions
    ];
    if (finalDataSet.length > 0) {
      setDataStatus("");
    }
    updateDataSet(finalDataSet);
  }, [dataSet, googleDataSet]);


  useEffect(() => {
    if (parentWidth > MobileViewWidth) {
      setLocationInputText(Config.locationInputPlaceholder[tenant as keyof IConfig["locationInputPlaceholder"]].desktop_text)

      if (inputLength > 2 && inputFocusStatus) {
        setIsSearchResultVisible(true);
      } else {
        setIsSearchResultVisible(false);
      }
    } else {
      setLocationInputText(Config.locationInputPlaceholder[tenant as keyof IConfig["locationInputPlaceholder"]].mobile_text)

      if (inputLength > 2 && inputEditableStatus) {
        setIsSearchResultVisible(true);
        dispatch(clubFinderSearchBarSliceActions.searchOptionsLoaded(true));
      } else {
        setIsSearchResultVisible(false);
        dispatch(clubFinderSearchBarSliceActions.searchOptionsLoaded(false));
      }
    }
  }, [inputLength, inputFocusStatus, inputEditableStatus, parentWidth, dispatch])

  useEffect(() => {
    const timer = setTimeout(async () => {
      const searchApiEndpoint = `${Config.baseUrl}${Config.apiEndpoints.v1.findResults}`;
      if (inputLength >= Config.search.minSearchLength && isNaN(Number(searchInput))) {
        setDataStatus("searching");
        getClubsByName(`${searchApiEndpoint}?query=${searchInput}&map_key=${googleMapConfig.mapsAPIKey}&country=${googleMapConfig.placesLookupCountry}&lang=${language}`);
      } else if(!isNaN(Number(searchInput)) && inputLength >= TenantAppVersionConfig[tenant].postCodeSearchLength) {
        setDataStatus("searching");
        getPlacesByPinCode(`${searchApiEndpoint}?query=${searchInput}&map_key=${googleMapConfig.mapsAPIKey}&country=${googleMapConfig.placesLookupCountry}&lang=${language}`);
      } else {
        updateDataSet([]);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [inputLength, searchInput, googleMapConfig, language, getClubsByName, getRegionsByName]);

  useEffect(() => {
    if (!loadingClubs && !loadingRegions) {
      consolidateDataSets();
    }
  }, [loadingClubs, loadingRegions]);

  /*
    When text is typed into location input
   */
  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(e.target.value);
    setInputLength(e.target.value.length);
  };

  const inputFocusStatusHandler = (status: boolean): void => {
    setIsResultItemClicked(false);
    if (!isMouseOverResults) {
      setInputFocusStatus(status);
    }
  }

  const inputEditableStatusHandler = (status: boolean): void => {
    setInputEditableStatus(status);
    if (parentWidth <= MobileViewWidth) {
      dispatch(clubFinderSearchBarSliceActions.activeSearchFilterMobile({
        location: status,
        format: false,
        gender: false
      }));
    }
  }

  const handleItemSelection = (selectedLocationData: ClubFinderLocationResultTypes) => {
    setPresetInputValue('');
    dispatch(clubFinderSearchBarSliceActions.setSelectedLocation(selectedLocationData));
    setInputFocusStatus(false);
    setIsMouseOverResults(false);
    setIsResultItemClicked(true);
    dispatch(clubFinderSearchBarSliceActions.setFilterButtonVisible(true));
    if (parentWidth <= MobileViewWidth) {
      dispatch(clubFinderSearchBarSliceActions.activeSearchFilterMobile({
        location: false,
        format: true,
        gender: false
      }));
    }
  }

  const handlePinCodeSuggestedItemSelection = (item: string) => {
    const searchableItem = item;
    setSearchInput(searchableItem);
    setPresetInputValue(searchableItem);
  }

  const isMouseOver = (isMouseOver: boolean): void => {
    setIsMouseOverResults(isMouseOver);
  }


  return (
    <div className={`${baseClass}`}>
      <SearchLocationInput
        label={"Location"}
        inputBoxIcon={icon}
        placeholder={locationInputText}
        onChangeHandler={onChangeHandler}
        inputFocusStatusHandler={inputFocusStatusHandler}
        isResultItemClicked={isResultItemClicked}
        setResultItemClicked={setIsResultItemClicked}
        inputEditableStatusHandler={inputEditableStatusHandler}
        search={props.search}
        openFilterDrawer={props.openFilterDrawer}
        getUserCurrentLocation={props.getUserCurrentLocation}
        presetValue={presetInputValue}
        isResultSetVisible={isSearchResultVisible}
      />
      {isSearchResultVisible && (
        <SearchLocationResults
          handlePinCodeSuggestedItemSelection={(item) => handlePinCodeSuggestedItemSelection(item)}
          pinCodeLocationResults={pinCodeLocationResults}
          searchResults={dataSet}
          searchStatus={dataStatus}
          presetValue={presetInputValue}
          handleItemSelection={handleItemSelection}
          isMouseover={isMouseOver}
          isSearching={dataStatus === 'searching'}
        />
      )}
    </div>
  )
}

export default SearchSuggestLocation;
